import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Info } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { useLang } from '../context/LanguageContext';
import Accordion from './Accordion';

function MetricCard({ label, value, num, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.4 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="glass rounded-2xl p-8 text-center card-lift group"
      data-testid={`financial-metric-${index}`}
    >
      <p className="text-[#BFC3C9] text-xs uppercase tracking-widest mb-4 font-medium">{label}</p>
      <div className="counter-value text-4xl md:text-5xl font-black font-heading mb-2">
        {inView ? (
          <CountUp
            end={num}
            duration={2}
            separator=" "
            suffix=" €"
            start={0}
          />
        ) : (
          <span>0 €</span>
        )}
      </div>
      <p className="text-[#D4AF37] text-sm font-semibold">{value}</p>
    </motion.div>
  );
}

export default function FinancialModel() {
  const { t } = useLang();
  const fin = t.financial;

  return (
    <section id="finances" className="py-24 md:py-32 relative overflow-hidden" style={{ background: '#12141A' }}>
      {/* Background image with overlay */}
      <div className="section-bg">
        <img src="/img/photo_21_2026-03-14_14-37-22.jpg" alt="" />
      </div>

      <div
        className="orb-gold absolute"
        style={{ width: 700, height: 700, bottom: '-20%', left: '-10%', opacity: 0.06, zIndex: 1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">Finances</span>
          </div>
          <h2 
            className="text-4xl md:text-6xl font-black text-white mb-4 font-heading"
            style={{
              fontFamily: "'Sora', sans-serif",
              textShadow: '0 2px 30px rgba(0,0,0,0.5)'
            }}
          >
            {fin.title}
          </h2>
          <p className="text-[#BFC3C9] text-lg">{fin.subtitle}</p>
        </motion.div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {fin.metrics.map((m, i) => (
            <MetricCard key={i} label={m.label} value={m.value} num={m.num} index={i} />
          ))}
        </div>

        {/* Notes */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="glass rounded-2xl p-7 flex gap-5 items-start"
          data-testid="financial-notes"
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background: 'rgba(107,78,255,0.1)', border: '1px solid rgba(107,78,255,0.2)' }}
          >
            <Info size={22} className="text-[#9B7FFF]" />
          </div>
          <div>
            <p className="text-white font-medium mb-2 leading-relaxed">{fin.note1}</p>
            <p className="text-[#BFC3C9] leading-relaxed flex items-center gap-2">
              <TrendingUp size={16} className="text-[#D4AF37] flex-shrink-0" />
              {fin.note2}
            </p>
          </div>
        </motion.div>

        {/* Detailed Breakdown in Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-8"
        >
          <Accordion title={fin.detailsTitle || 'Детальная разбивка'}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Revenue Breakdown */}
              <div>
                <h4 className="text-[#D4AF37] font-bold text-lg mb-4">{fin.revenueBreakdown.title}</h4>
                <div className="space-y-3">
                  {fin.revenueBreakdown.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-[#E5E7EB]">{item.label}</span>
                      <span className="text-white font-bold">{item.percent}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 rounded-xl bg-white/5">
                  <p className="text-[#22C55E] font-bold text-sm mb-1">{fin.paybackTitle}</p>
                  <p className="text-white font-black text-2xl">{fin.paybackValue}</p>
                </div>
              </div>

              {/* Expenses Breakdown */}
              <div>
                <h4 className="text-[#A78BFA] font-bold text-lg mb-4">{fin.expensesBreakdown.title}</h4>
                
                <div className="mb-4">
                  <p className="text-[#9CA3AF] text-sm font-semibold mb-2 uppercase tracking-wider">
                    {fin.expensesBreakdown.fixed[0] === 'Аренда помещения' ? 'Постоянные расходы' : 'Gastos fijos'}
                  </p>
                  <ul className="space-y-2">
                    {fin.expensesBreakdown.fixed.map((item, i) => (
                      <li key={i} className="text-[#E5E7EB] flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-1.5">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-[#9CA3AF] text-sm font-semibold mb-2 uppercase tracking-wider">
                    {fin.expensesBreakdown.variable[0] === 'Продукты (ресторан)' ? 'Переменные расходы' : 'Gastos variables'}
                  </p>
                  <ul className="space-y-2">
                    {fin.expensesBreakdown.variable.map((item, i) => (
                      <li key={i} className="text-[#E5E7EB] flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-1.5">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
